import { graphql } from "gatsby"
import SbEditable from "storyblok-react"

import useStoryblok from "../lib/storyblok"
import DynamicComponent from "../components/storyblok/dynamicComponent"
import Layout from "../components/layout/layout"
import Seo from "../components/seo"

const IndexPage = ({ data, location }) => {
	let story = data.storyblokEntry
	story = useStoryblok(story, location)

	// Extract SEO data from Storyblok content (same pattern as page.js)
	const seoBlock = story.content.body?.find(
		blok => blok.component === "seoBlock"
	)

	// Build SEO props from SEO block or use defaults
	const getSeoValue = (key, defaultValue = null) => {
		const value = seoBlock?.[key] ?? seoBlock?.content?.[key]
		
		// Handle empty strings as null
		if (value === "" || (Array.isArray(value) && value.length === 0)) {
			return defaultValue
		}
		// Handle arrays (like ogType: [""] or ogImage: [{...}])
		if (Array.isArray(value)) {
			const firstItem = value[0]
			if (firstItem === "" || firstItem === null || firstItem === undefined) {
				return defaultValue
			}
			// For ogImage array, return the filename from the first item
			if (key === "ogImage" && firstItem?.filename) {
				return firstItem.filename
			}
			// For other arrays, return the first non-empty item
			return firstItem || defaultValue
		}
		return value || defaultValue
	}

	// Default OG image fallback: use homePageImage if no SEO block image is set
	const defaultOgImage = story.content.body?.find(
		content => content?.component === "homePageImage"
	)?.content?.filename

	const seoProps = {
		title: getSeoValue("title", "Home"),
		description: getSeoValue("description", "Institute of Public Works Engineering Australasia"),
		keywords: getSeoValue("keywords", null),
		canonical: getSeoValue("canonical", null),
		ogImage: getSeoValue("ogImage", defaultOgImage),
		ogType: getSeoValue("ogType", "website"),
		twitterCard: getSeoValue("twitterCard", "summary_large_image"),
		twitterSite: getSeoValue("twitterSite", null),
		robots: getSeoValue("robots", null),
		noindex: getSeoValue("noindex", false),
		nofollow: getSeoValue("nofollow", false),
		structuredData: getSeoValue("structuredData", null),
	}

	// Build structured data if not provided
	if (!seoProps.structuredData) {
		seoProps.structuredData = {
			"@context": "https://schema.org",
			"@type": "WebPage",
			name: seoProps.title,
			description: seoProps.description,
			url: typeof window !== "undefined" ? window.location.href : "https://ipweansw.org/",
		}
	}

	const components = story.content.body?.map(blok => {
		// Skip SEO block as it doesn't render anything visible
		if (blok.component === "seoBlock") {
			return null
		}
		return <DynamicComponent blok={blok} key={blok._uid} />
	})

	return (
		<Layout>
			<SbEditable content={story.content}>
				<Seo {...seoProps} />
				<h1>{story.content.title}</h1>
				{components}
			</SbEditable>
		</Layout>
	)
}

export default IndexPage

export const query = graphql`
	query HomeQuery {
		storyblokEntry(full_slug: { eq: "home" }) {
			content
			name
		}
	}
`
